import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useConsoleServices } from './console-services.jsx';
import type { StepViewProps } from './flow-route-types.js';
import { TextField } from './form-field.jsx';
import { Notice } from './notice.jsx';
import { sectionErrors, type StoreDraftShape } from './onboarding-state.js';
import { useOnboarding } from './use-onboarding.jsx';

const googleMapsScriptId = 'google-maps-places-sdk';
const googleMapsPlacesCallbackName = '__marketsideGoogleMapsPlacesReady';
const googleMapsPlacesLoadTimeoutMs = 15_000;
const googleMapsAddressSearchErrorPrefix = '[Google Maps] Falha ao carregar busca de endereço';

type GoogleAddressLoadStatus = 'idle' | 'loading' | 'ready' | 'error';
type GoogleMapsLoadStage = 'create-script' | 'existing-script' | 'script-load' | 'script-error' | 'callback-timeout' | 'initialize-autocomplete' | 'initialize-map';
type GoogleMapsMissingApi = 'google.maps' | 'google.maps.places.PlaceAutocompleteElement' | 'google.maps.Map';

interface GooglePlaceLocation {
  lat?: number | (() => number);
  lng?: number | (() => number);
}

interface GooglePlaceResult {
  id?: string;
  placeId?: string;
  place_id?: string;
  formattedAddress?: string;
  formatted_address?: string;
  location?: GooglePlaceLocation;
  geometry?: {
    location?: GooglePlaceLocation;
  };
  fetchFields?(request: { fields: string[] }): Promise<void>;
}

interface GooglePlacePrediction {
  toPlace(): GooglePlaceResult;
}

interface GooglePlaceAutocompleteElement extends HTMLElement {
  componentRestrictions?: { country: string | string[] };
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
  placeholder?: string;
  types?: string[];
  value?: string;
}

interface GoogleMapCenter {
  lat: number;
  lng: number;
}

interface GoogleMapInstance {
  setCenter(center: GoogleMapCenter): void;
  setZoom(zoom: number): void;
}

interface GoogleMapsWindow extends Window {
  __marketsideGoogleMapsPlacesReady?: () => void;
  google?: {
    maps?: {
      Map?: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapInstance;
      places?: {
        PlaceAutocompleteElement: new () => GooglePlaceAutocompleteElement;
      };
    };
  };
}

export function StepStore({ showErrors }: StepViewProps) {
  const { draft, patchDraft } = useOnboarding();
  const { googleMapsApiKey } = useConsoleServices();
  const errors = errorsFor(draft, showErrors);
  const store = draft.store;

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="name"
        label="Nome da loja"
        required
        value={store.name}
        error={errors.name}
        maxLength={80}
        autoComplete="organization"
        onChange={(value) => patchDraft('store', { name: value })}
      />

      <GoogleAddressField
        apiKey={googleMapsApiKey}
        store={store}
        errors={errors}
        onChange={(patch) => patchDraft('store', patch)}
      />
    </div>
  );
}

function GoogleAddressField({
  apiKey,
  store,
  errors,
  onChange,
}: {
  apiKey: string | null;
  store: StoreDraftShape;
  errors: Record<string, string>;
  onChange: (patch: Partial<StoreDraftShape>) => void;
}) {
  const autocompleteContainerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteElementRef = useRef<GooglePlaceAutocompleteElement | null>(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<GoogleMapInstance | null>(null);
  const onChangeRef = useRef(onChange);
  const [loadStatus, setLoadStatus] = useState<GoogleAddressLoadStatus>('idle');
  const mapCenter = useMemo(() => mapCenterFor(store), [store.googlePlaceId, store.latitude, store.longitude]);

  onChangeRef.current = onChange;

  useEffect(() => {
    if (!apiKey) {
      setLoadStatus('error');
      return;
    }

    let active = true;
    setLoadStatus('loading');

    loadGooglePlaces(apiKey)
      .then(() => {
        if (active) {
          setLoadStatus('ready');
        }
      })
      .catch((error: unknown) => {
        reportGoogleMapsAddressSearchFailure({
          status: 'error',
          stage: googleMapsLoadStageFor(error),
          scriptSrc: googleMapsScriptSrcFor(error),
          eventType: googleMapsEventTypeFor(error),
          missingApis: googleMapsMissingApisFor(error),
          error,
        });

        if (active) {
          setLoadStatus('error');
        }
      });

    return () => {
      active = false;
    };
  }, [apiKey]);

  useEffect(() => {
    if (loadStatus !== 'ready' || !autocompleteContainerRef.current) {
      return;
    }

    const maps = (window as GoogleMapsWindow).google?.maps;

    if (!maps?.places?.PlaceAutocompleteElement) {
      reportMissingGoogleMapsApis('initialize-autocomplete');
      setLoadStatus('error');
      return;
    }

    let active = true;
    const autocompleteElement = new maps.places.PlaceAutocompleteElement();
    configureGooglePlaceAutocompleteElement(autocompleteElement);
    syncGooglePlaceAutocompleteElement(autocompleteElement, store, errors);
    autocompleteContainerRef.current.replaceChildren(autocompleteElement);
    autocompleteElementRef.current = autocompleteElement;

    const handleInput = (event: Event) => {
      const value = googleAutocompleteInputValue(event);
      onChangeRef.current({
        formattedAddress: value,
        googlePlaceId: '',
        latitude: '',
        longitude: '',
      });
    };

    const handleSelect = (event: Event) => {
      void handleGooglePlaceAutocompleteSelect(event, onChangeRef).catch((error: unknown) => {
        reportGoogleMapsAddressSearchFailure({
          status: 'error',
          stage: 'initialize-autocomplete',
          error,
        });

        if (active) {
          setLoadStatus('error');
        }
      });
    };

    autocompleteElement.addEventListener('input', handleInput);
    autocompleteElement.addEventListener('gmp-select', handleSelect);

    return () => {
      active = false;
      autocompleteElement.removeEventListener('input', handleInput);
      autocompleteElement.removeEventListener('gmp-select', handleSelect);
      autocompleteElement.remove();

      if (autocompleteElementRef.current === autocompleteElement) {
        autocompleteElementRef.current = null;
      }
    };
  }, [loadStatus]);

  useEffect(() => {
    const autocompleteElement = autocompleteElementRef.current;

    if (!autocompleteElement) {
      return;
    }

    syncGooglePlaceAutocompleteElement(autocompleteElement, store, errors);
  }, [errors.formattedAddress, errors.googlePlaceId, errors.latitude, errors.longitude, store.formattedAddress]);

  useEffect(() => {
    if (!mapCenter) {
      mapInstanceRef.current = null;
      return;
    }

    if (loadStatus !== 'ready' || !mapRef.current) {
      return;
    }

    const Map = (window as GoogleMapsWindow).google?.maps?.Map;

    if (!Map) {
      reportMissingGoogleMapsApis('initialize-map');
      setLoadStatus('error');
      return;
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new Map(mapRef.current, {
        center: mapCenter,
        zoom: 17,
        disableDefaultUI: true,
        draggable: false,
        gestureHandling: 'none',
        clickableIcons: false,
        keyboardShortcuts: false,
        scrollwheel: false,
      });
      return;
    }

    mapInstanceRef.current.setCenter(mapCenter);
    mapInstanceRef.current.setZoom(17);
  }, [loadStatus, mapCenter]);

  return (
    <div className="flex flex-col gap-3">
      <AddressConfigNotice apiKey={apiKey} loadStatus={loadStatus} />
      <div className="fieldset w-full">
        <label className="fieldset-legend" htmlFor="enderecoGoogle">
          Endereço da loja <span className="text-error" aria-hidden="true">*</span>
        </label>
        {loadStatus === 'ready' && (
          <div ref={autocompleteContainerRef} className="w-full" />
        )}
        {loadStatus !== 'ready' && (
          <input
            id="enderecoGoogle"
            name="enderecoGoogle"
            className="input w-full"
            value={store.formattedAddress}
            placeholder="Digite o endereço e escolha uma sugestão"
            autoComplete="off"
            aria-invalid={Boolean(errors.googlePlaceId || errors.formattedAddress || errors.latitude || errors.longitude)}
            aria-describedby="enderecoGoogle-hint"
            onChange={(event) => onChange({
              formattedAddress: event.target.value,
              googlePlaceId: '',
              latitude: '',
              longitude: '',
            })}
          />
        )}
        <AddressMessage errors={errors} selected={Boolean(store.googlePlaceId)} />
      </div>
      <AddressMapPreview
        address={store.formattedAddress}
        selected={Boolean(mapCenter)}
        loadStatus={loadStatus}
        mapRef={mapRef}
      />
    </div>
  );
}

function AddressMapPreview({
  address,
  selected,
  loadStatus,
  mapRef,
}: {
  address: string;
  selected: boolean;
  loadStatus: GoogleAddressLoadStatus;
  mapRef: RefObject<HTMLDivElement | null>;
}) {
  const showMap = selected && loadStatus === 'ready';
  const message = mapPreviewMessage({ address, selected, loadStatus });

  return (
    <section
      className="overflow-hidden rounded-box border border-base-300 bg-base-200"
      aria-label="Mapa da loja"
    >
      <div className="relative h-56 w-full">
        {showMap ? (
          <>
            <div ref={mapRef} className="h-full w-full" aria-hidden="true" />
            <span
              className="pointer-events-none absolute left-1/2 top-1/2 flex size-10 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full bg-primary text-primary-content shadow-lg"
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="currentColor">
                <path d="M12 2a7 7 0 0 0-7 7c0 5.3 7 13 7 13s7-7.7 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
              </svg>
            </span>
          </>
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm opacity-70">
            {message}
          </div>
        )}
      </div>
      <p className="border-t border-base-300 bg-base-100 px-4 py-2 text-xs opacity-70">
        Mapa somente para conferência. Para corrigir o ponto, escolha outra sugestão no campo de endereço.
      </p>
    </section>
  );
}

function AddressConfigNotice({
  apiKey,
  loadStatus,
}: {
  apiKey: string | null;
  loadStatus: GoogleAddressLoadStatus;
}) {
  if (!apiKey) {
    return (
      <Notice tone="danger" title="Busca de endereço indisponível">
        Não é possível confirmar o endereço agora. Tente de novo mais tarde ou fale com a Marketside.
      </Notice>
    );
  }

  if (loadStatus !== 'error') {
    return null;
  }

  return (
    <Notice tone="danger" title="Não foi possível carregar a busca de endereço">
      Verifique sua conexão e recarregue a página.
    </Notice>
  );
}

function AddressMessage({ errors, selected }: { errors: Record<string, string>; selected: boolean }) {
  const error = errors.googlePlaceId ?? errors.formattedAddress ?? errors.latitude ?? errors.longitude;

  if (error) {
    return <p id="enderecoGoogle-hint" className="label text-error">{error}</p>;
  }

  if (selected) {
    return <p id="enderecoGoogle-hint" className="label text-success">Endereço confirmado.</p>;
  }

  return <p id="enderecoGoogle-hint" className="label">Escolha uma das sugestões para confirmar o endereço exato da loja.</p>;
}

function mapPreviewMessage({
  address,
  selected,
  loadStatus,
}: {
  address: string;
  selected: boolean;
  loadStatus: GoogleAddressLoadStatus;
}): string {
  if (!address.trim()) {
    return 'Digite o endereço da loja para visualizar o mapa.';
  }

  if (!selected) {
    return 'Escolha uma sugestão do Google Maps para visualizar o ponto exato.';
  }

  if (loadStatus === 'loading' || loadStatus === 'idle') {
    return 'Carregando mapa da loja…';
  }

  return 'Não foi possível exibir o mapa agora. O endereço selecionado continua salvo.';
}

function mapCenterFor(store: StoreDraftShape): GoogleMapCenter | null {
  if (!store.googlePlaceId) {
    return null;
  }

  const lat = Number(store.latitude);
  const lng = Number(store.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
}

function configureGooglePlaceAutocompleteElement(element: GooglePlaceAutocompleteElement): void {
  element.id = 'enderecoGoogle';
  element.classList.add('block', 'w-full');
  element.setAttribute('name', 'enderecoGoogle');
  element.setAttribute('aria-describedby', 'enderecoGoogle-hint');
  element.placeholder = 'Digite o endereço e escolha uma sugestão';
  element.includedRegionCodes = ['br'];
}

function syncGooglePlaceAutocompleteElement(
  element: GooglePlaceAutocompleteElement,
  store: StoreDraftShape,
  errors: Record<string, string>,
): void {
  const invalid = Boolean(errors.googlePlaceId || errors.formattedAddress || errors.latitude || errors.longitude);
  element.setAttribute('aria-invalid', String(invalid));

  if (element.value === store.formattedAddress) {
    return;
  }

  element.value = store.formattedAddress;
}

async function handleGooglePlaceAutocompleteSelect(
  event: Event,
  onChangeRef: { current: (patch: Partial<StoreDraftShape>) => void },
): Promise<void> {
  const place = googlePlaceFromAutocompleteEvent(event);

  if (!place) {
    clearGooglePlaceSelection(onChangeRef);
    return;
  }

  await fetchGooglePlaceFields(place);
  const selectedPlace = selectedGooglePlaceFor(place);

  if (!selectedPlace) {
    clearGooglePlaceSelection(onChangeRef);
    return;
  }

  onChangeRef.current({
    googlePlaceId: selectedPlace.placeId,
    formattedAddress: selectedPlace.formattedAddress,
    latitude: String(selectedPlace.lat),
    longitude: String(selectedPlace.lng),
  });
}

function clearGooglePlaceSelection(onChangeRef: { current: (patch: Partial<StoreDraftShape>) => void }): void {
  onChangeRef.current({ googlePlaceId: '', latitude: '', longitude: '' });
}

function googleAutocompleteInputValue(event: Event): string {
  const targetValue = googleAutocompleteValueFromTarget(event.target);

  if (targetValue !== null) {
    return targetValue;
  }

  for (const target of event.composedPath()) {
    const value = googleAutocompleteValueFromTarget(target);

    if (value !== null) {
      return value;
    }
  }

  return '';
}

function googleAutocompleteValueFromTarget(target: EventTarget | null): string | null {
  if (!target) {
    return null;
  }

  if (typeof target !== 'object') {
    return null;
  }

  const value = (target as { value?: unknown }).value;

  if (typeof value === 'string') {
    return value;
  }

  return null;
}

function googlePlaceFromAutocompleteEvent(event: Event): GooglePlaceResult | null {
  const eventPlace = googlePlaceFromUnknown((event as { place?: unknown }).place);

  if (eventPlace) {
    return eventPlace;
  }

  const eventPrediction = googlePlacePredictionFromUnknown((event as { placePrediction?: unknown }).placePrediction);

  if (eventPrediction) {
    return eventPrediction.toPlace();
  }

  if (!(event instanceof CustomEvent)) {
    return null;
  }

  const detail = googleAutocompleteEventDetail(event);
  const detailPlace = googlePlaceFromUnknown(detail.place);

  if (detailPlace) {
    return detailPlace;
  }

  const detailPrediction = googlePlacePredictionFromUnknown(detail.placePrediction);

  if (detailPrediction) {
    return detailPrediction.toPlace();
  }

  return null;
}

function googleAutocompleteEventDetail(event: CustomEvent<unknown>): { place?: unknown; placePrediction?: unknown } {
  if (!event.detail) {
    return {};
  }

  if (typeof event.detail !== 'object') {
    return {};
  }

  return event.detail as { place?: unknown; placePrediction?: unknown };
}

function googlePlaceFromUnknown(value: unknown): GooglePlaceResult | null {
  if (!value) {
    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  return value as GooglePlaceResult;
}

function googlePlacePredictionFromUnknown(value: unknown): GooglePlacePrediction | null {
  if (!value) {
    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const toPlace = (value as { toPlace?: unknown }).toPlace;

  if (typeof toPlace !== 'function') {
    return null;
  }

  return value as GooglePlacePrediction;
}

async function fetchGooglePlaceFields(place: GooglePlaceResult): Promise<void> {
  if (!place.fetchFields) {
    return;
  }

  await place.fetchFields({ fields: ['id', 'formattedAddress', 'location'] });
}

function selectedGooglePlaceFor(place: GooglePlaceResult): { formattedAddress: string; lat: number; lng: number; placeId: string } | null {
  const placeId = googlePlaceIdFor(place);

  if (!placeId) {
    return null;
  }

  const formattedAddress = googleFormattedAddressFor(place);

  if (!formattedAddress) {
    return null;
  }

  const coordinates = googlePlaceCoordinatesFor(place);

  if (!coordinates) {
    return null;
  }

  return {
    formattedAddress,
    lat: coordinates.lat,
    lng: coordinates.lng,
    placeId,
  };
}

function googlePlaceIdFor(place: GooglePlaceResult): string | null {
  if (place.id) {
    return place.id;
  }

  if (place.placeId) {
    return place.placeId;
  }

  if (place.place_id) {
    return place.place_id;
  }

  return null;
}

function googleFormattedAddressFor(place: GooglePlaceResult): string | null {
  if (place.formattedAddress) {
    return place.formattedAddress;
  }

  if (place.formatted_address) {
    return place.formatted_address;
  }

  return null;
}

function googlePlaceCoordinatesFor(place: GooglePlaceResult): GoogleMapCenter | null {
  if (place.location) {
    return googleCoordinatesForLocation(place.location);
  }

  if (place.geometry?.location) {
    return googleCoordinatesForLocation(place.geometry.location);
  }

  return null;
}

function googleCoordinatesForLocation(location: GooglePlaceLocation): GoogleMapCenter | null {
  const lat = googleCoordinateValue(location.lat);
  const lng = googleCoordinateValue(location.lng);

  if (lat === null || lng === null) {
    return null;
  }

  return { lat, lng };
}

function googleCoordinateValue(value: number | (() => number) | undefined): number | null {
  if (typeof value === 'function') {
    return googleFiniteCoordinate(value());
  }

  if (typeof value === 'number') {
    return googleFiniteCoordinate(value);
  }

  return null;
}

function googleFiniteCoordinate(value: number): number | null {
  if (Number.isFinite(value)) {
    return value;
  }

  return null;
}

interface GoogleMapsPlacesWaiter {
  reject(error: GoogleMapsPlacesLoadError): void;
  resolve(): void;
  script: HTMLScriptElement;
  timeoutId: number;
}

const googleMapsPlacesWaiters = new Set<GoogleMapsPlacesWaiter>();

function loadGooglePlaces(apiKey: string): Promise<void> {
  if (googleMapsApisReady()) {
    return Promise.resolve();
  }

  const existing = googleMapsScriptElement();

  if (existing) {
    return waitForGooglePlaces(existing, 'existing-script');
  }

  const script = document.createElement('script');
  script.id = googleMapsScriptId;
  script.src = googleMapsScriptUrl(apiKey);
  script.async = true;
  script.defer = true;

  const loading = waitForGooglePlaces(script, 'create-script');
  document.body.appendChild(script);

  return loading;
}

function googleMapsScriptUrl(apiKey: string): string {
  const url = new URL('https://maps.googleapis.com/maps/api/js');
  url.searchParams.set('key', apiKey);
  url.searchParams.set('loading', 'async');
  url.searchParams.set('libraries', 'places');
  url.searchParams.set('v', 'weekly');
  url.searchParams.set('callback', googleMapsPlacesCallbackName);

  return url.toString();
}

function waitForGooglePlaces(script: HTMLScriptElement, stage: GoogleMapsLoadStage): Promise<void> {
  ensureGoogleMapsPlacesCallback();
  ensureGoogleMapsScriptListeners(script);

  return new Promise((resolve, reject) => {
    if (googleMapsApisReady()) {
      resolve();
      return;
    }

    if (script.dataset.googleMapsPlacesStatus === 'ready') {
      reject(googleMapsMissingApisError('script-load', script));
      return;
    }

    if (script.dataset.googleMapsPlacesStatus === 'error') {
      reject(googleMapsScriptLoadErrorFor(script, stage));
      return;
    }

    script.dataset.googleMapsPlacesStatus = 'loading';
    script.dataset.googleMapsPlacesWaitStage = stage;

    const waiter: GoogleMapsPlacesWaiter = {
      reject,
      resolve,
      script,
      timeoutId: window.setTimeout(() => {
        script.dataset.googleMapsPlacesStatus = 'error';
        script.dataset.googleMapsPlacesErrorStage = 'callback-timeout';
        rejectGoogleMapsPlacesWaiters(googleMapsCallbackTimeoutError(script));
      }, googleMapsPlacesLoadTimeoutMs),
    };

    googleMapsPlacesWaiters.add(waiter);
  });
}

function ensureGoogleMapsPlacesCallback(): void {
  (window as GoogleMapsWindow).__marketsideGoogleMapsPlacesReady = handleGoogleMapsPlacesReady;
}

function ensureGoogleMapsScriptListeners(script: HTMLScriptElement): void {
  if (script.dataset.googleMapsPlacesListenersAttached === 'true') {
    return;
  }

  script.dataset.googleMapsPlacesListenersAttached = 'true';

  script.addEventListener('load', () => {
    markGoogleMapsScriptLoaded(script);
  }, { once: true });

  script.addEventListener('error', (event) => {
    script.dataset.googleMapsPlacesStatus = 'error';
    script.dataset.googleMapsPlacesErrorStage = 'script-error';
    rejectGoogleMapsPlacesWaiters(new GoogleMapsPlacesLoadError('Google Maps SDK script failed to load.', {
      eventType: event.type,
      stage: 'script-error',
      script,
    }));
  }, { once: true });
}

function markGoogleMapsScriptLoaded(script: HTMLScriptElement): void {
  if (script.dataset.googleMapsPlacesStatus === 'error') {
    return;
  }

  if (script.dataset.googleMapsPlacesStatus === 'ready') {
    return;
  }

  script.dataset.googleMapsPlacesStatus = 'script-loaded';
}

function handleGoogleMapsPlacesReady(): void {
  const script = googleMapsScriptElement();

  if (googleMapsApisReady()) {
    if (script) {
      script.dataset.googleMapsPlacesStatus = 'ready';
    }

    resolveGoogleMapsPlacesWaiters();
    return;
  }

  if (script) {
    script.dataset.googleMapsPlacesStatus = 'error';
    script.dataset.googleMapsPlacesErrorStage = 'script-load';
  }

  rejectGoogleMapsPlacesWaiters(googleMapsMissingApisError('script-load', script));
}

function resolveGoogleMapsPlacesWaiters(): void {
  for (const waiter of Array.from(googleMapsPlacesWaiters)) {
    window.clearTimeout(waiter.timeoutId);
    googleMapsPlacesWaiters.delete(waiter);
    waiter.resolve();
  }
}

function rejectGoogleMapsPlacesWaiters(error: GoogleMapsPlacesLoadError): void {
  for (const waiter of Array.from(googleMapsPlacesWaiters)) {
    window.clearTimeout(waiter.timeoutId);
    googleMapsPlacesWaiters.delete(waiter);
    waiter.reject(error);
  }
}

function googleMapsScriptLoadErrorFor(
  script: HTMLScriptElement,
  fallbackStage: GoogleMapsLoadStage,
): GoogleMapsPlacesLoadError {
  const errorStage = googleMapsStoredErrorStage(script);

  if (errorStage === 'script-error') {
    return new GoogleMapsPlacesLoadError('Google Maps SDK script had already failed to load.', {
      stage: 'script-error',
      script,
    });
  }

  if (errorStage === 'callback-timeout') {
    return googleMapsCallbackTimeoutError(script);
  }

  if (errorStage === 'script-load') {
    return googleMapsMissingApisError('script-load', script);
  }

  return new GoogleMapsPlacesLoadError('Google Maps SDK script is not available.', {
    stage: fallbackStage,
    script,
  });
}

function googleMapsStoredErrorStage(script: HTMLScriptElement): GoogleMapsLoadStage | undefined {
  const { googleMapsPlacesErrorStage } = script.dataset;

  if (isGoogleMapsLoadStage(googleMapsPlacesErrorStage)) {
    return googleMapsPlacesErrorStage;
  }

  return undefined;
}

function isGoogleMapsLoadStage(value: string | undefined): value is GoogleMapsLoadStage {
  if (value === 'create-script') {
    return true;
  }

  if (value === 'existing-script') {
    return true;
  }

  if (value === 'script-load') {
    return true;
  }

  if (value === 'script-error') {
    return true;
  }

  if (value === 'callback-timeout') {
    return true;
  }

  if (value === 'initialize-autocomplete') {
    return true;
  }

  if (value === 'initialize-map') {
    return true;
  }

  return false;
}

function googleMapsCallbackTimeoutError(script: HTMLScriptElement | null): GoogleMapsPlacesLoadError {
  const missingApis = missingGoogleMapsApis();
  let message = `Google Maps SDK callback ${googleMapsPlacesCallbackName} did not fire within ${googleMapsPlacesLoadTimeoutMs}ms.`;

  if (missingApis.length > 0) {
    message += ` Missing APIs: ${missingApis.join(', ')}`;
  }

  return new GoogleMapsPlacesLoadError(message, {
    missingApis,
    stage: 'callback-timeout',
    script,
  });
}

function googleMapsApisReady(): boolean {
  return missingGoogleMapsApis().length === 0;
}

function missingGoogleMapsApis(): GoogleMapsMissingApi[] {
  const missingApis: GoogleMapsMissingApi[] = [];
  const maps = (window as GoogleMapsWindow).google?.maps;

  if (!maps) {
    missingApis.push('google.maps');
    return missingApis;
  }

  if (!maps.places?.PlaceAutocompleteElement) {
    missingApis.push('google.maps.places.PlaceAutocompleteElement');
  }

  if (!maps.Map) {
    missingApis.push('google.maps.Map');
  }

  return missingApis;
}

function reportMissingGoogleMapsApis(stage: GoogleMapsLoadStage): void {
  const script = googleMapsScriptElement();
  const missingApis = missingGoogleMapsApis();

  if (missingApis.length === 0) {
    return;
  }

  reportGoogleMapsAddressSearchFailure({
    status: 'error',
    stage,
    scriptSrc: safeGoogleMapsScriptSrc(script),
    missingApis,
    error: googleMapsMissingApisError(stage, script),
  });
}

function googleMapsMissingApisError(stage: GoogleMapsLoadStage, script: HTMLScriptElement | null): GoogleMapsPlacesLoadError {
  const missingApis = missingGoogleMapsApis();
  return new GoogleMapsPlacesLoadError(`Google Maps SDK loaded without required APIs: ${missingApis.join(', ')}`, {
    missingApis,
    stage,
    script,
  });
}

function googleMapsScriptElement(): HTMLScriptElement | null {
  return document.getElementById(googleMapsScriptId) as HTMLScriptElement | null;
}

class GoogleMapsPlacesLoadError extends Error {
  readonly eventType?: string;
  readonly missingApis?: GoogleMapsMissingApi[];
  readonly scriptSrc?: string;
  readonly stage: GoogleMapsLoadStage;

  constructor(message: string, {
    eventType,
    missingApis,
    script,
    stage,
  }: {
    eventType?: string;
    missingApis?: GoogleMapsMissingApi[];
    script: HTMLScriptElement | null;
    stage: GoogleMapsLoadStage;
  }) {
    super(message);
    this.name = 'GoogleMapsPlacesLoadError';
    this.eventType = eventType;
    this.missingApis = missingApis;
    this.scriptSrc = safeGoogleMapsScriptSrc(script);
    this.stage = stage;
  }
}

function reportGoogleMapsAddressSearchFailure({
  error,
  eventType,
  missingApis,
  scriptSrc,
  stage,
  status,
}: {
  error?: unknown;
  eventType?: string;
  missingApis?: GoogleMapsMissingApi[];
  scriptSrc?: string;
  stage: GoogleMapsLoadStage;
  status: GoogleAddressLoadStatus;
}): void {
  const daygnostic: Record<string, unknown> = { status, stage };

  if (scriptSrc) {
    daygnostic.scriptSrc = scriptSrc;
  }

  if (eventType) {
    daygnostic.eventType = eventType;
  }

  if (missingApis && missingApis.length > 0) {
    daygnostic.missingApis = missingApis;
  }

  addSafeErrorDiagnostic(daygnostic, error);
  console.error(googleMapsAddressSearchErrorPrefix, daygnostic);
}

function addSafeErrorDiagnostic(daygnostic: Record<string, unknown>, error: unknown): void {
  if (error instanceof Error) {
    daygnostic.errorName = error.name;
    daygnostic.errorMessage = redactGoogleMapsApiKey(error.message);

    const cause = safeErrorCause(error.cause);
    if (cause !== undefined) {
      daygnostic.errorCause = cause;
    }

    return;
  }

  if (typeof error === 'string') {
    daygnostic.errorMessage = redactGoogleMapsApiKey(error);
    return;
  }

  if (error !== undefined) {
    daygnostic.errorValueType = typeof error;
  }
}

function safeErrorCause(cause: unknown): unknown {
  if (cause instanceof Error) {
    const safeCause: Record<string, unknown> = {
      name: cause.name,
      message: redactGoogleMapsApiKey(cause.message),
    };
    const nestedCause = safeErrorCause(cause.cause);

    if (nestedCause !== undefined) {
      safeCause.cause = nestedCause;
    }

    return safeCause;
  }

  if (cause instanceof Event) {
    return { eventType: cause.type };
  }

  if (typeof cause === 'string') {
    return redactGoogleMapsApiKey(cause);
  }

  if (typeof cause === 'number' || typeof cause === 'boolean') {
    return cause;
  }

  return undefined;
}

function googleMapsLoadStageFor(error: unknown): GoogleMapsLoadStage {
  if (error instanceof GoogleMapsPlacesLoadError) {
    return error.stage;
  }

  return 'create-script';
}

function googleMapsScriptSrcFor(error: unknown): string | undefined {
  if (error instanceof GoogleMapsPlacesLoadError) {
    return error.scriptSrc;
  }

  return safeGoogleMapsScriptSrc(googleMapsScriptElement());
}

function googleMapsEventTypeFor(error: unknown): string | undefined {
  if (error instanceof GoogleMapsPlacesLoadError) {
    return error.eventType;
  }

  return undefined;
}

function googleMapsMissingApisFor(error: unknown): GoogleMapsMissingApi[] | undefined {
  if (error instanceof GoogleMapsPlacesLoadError) {
    return error.missingApis;
  }

  return undefined;
}

function safeGoogleMapsScriptSrc(script: HTMLScriptElement | null): string | undefined {
  if (!script) {
    return undefined;
  }

  return redactGoogleMapsApiKey(script.src);
}

function redactGoogleMapsApiKey(value: string): string {
  try {
    const url = new URL(value);

    if (url.searchParams.has('key')) {
      url.searchParams.set('key', 'REDACTED');
    }

    return url.toString();
  } catch {
    return value.replace(/([?&]key=)[^&]+/gu, '$1REDACTED');
  }
}

function errorsFor(draft: Parameters<typeof sectionErrors>[0], showErrors: boolean) {
  if (!showErrors) {
    return {};
  }

  return sectionErrors(draft, 'store');
}
